import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, Lock } from 'lucide-react'
import { getSupabaseClient } from '@/infrastructure/supabase/app'
import { Card } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isValidSession, setIsValidSession] = useState(true)

  useEffect(() => {
    // Check if we have a recovery session
    const checkSession = async () => {
      try {
        const client = getSupabaseClient()
        const { data: { session } } = await client.auth.getSession()
        // If there's no session and the URL doesn't contain tokens, then it might be invalid.
        // Supabase JS client handles parsing the token in the background, but we can verify.
        if (!session) {
          // Allow some time for Supabase client to parse URL hash fragment if they just landed
          setTimeout(async () => {
            const { data: { session: retrySession } } = await client.auth.getSession()
            if (!retrySession) {
              setIsValidSession(false)
              setError('Enlace de recuperacion invalido o expirado. Por favor solicita uno nuevo.')
            }
          }, 1500)
        }
      } catch (err) {
        console.error('Error verificando sesion de recuperacion:', err)
        setIsValidSession(false)
      }
    }

    void checkSession()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 6) {
      setError('La contrasena debe tener al menos 6 caracteres.')
      return
    }
    if (password !== confirmPassword) {
      setError('Las contrasenas no coinciden.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getSupabaseClient()
      const { error: updateError } = await client.auth.updateUser({ password })

      if (updateError) throw updateError
      setSuccess(true)
      
      // Auto redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (err) {
      console.error('Error al actualizar contrasena:', err)
      setError(err instanceof Error ? err.message : 'No se pudo actualizar la contrasena.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="app-shell flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-modal">
        <div className="text-center mb-6">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[14px] bg-gradient-to-br from-primary-500 to-[#8e67ff] text-2xl font-bold text-white shadow-glow">
            Z
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Nueva contrasena</h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Ingresa tu nueva contrasena de acceso.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:border-danger-900/70 dark:bg-danger-950/40 dark:text-danger-200">
            {error}
          </div>
        )}

        {success ? (
          <div className="space-y-4 text-center">
            <div className="flex flex-col items-center justify-center gap-2 text-success-600 dark:text-success-400 py-4">
              <CheckCircle className="w-16 h-16 animate-bounce" />
              <h2 className="text-lg font-bold">¡Contrasena actualizada!</h2>
            </div>
            <div className="text-sm text-neutral-500 dark:text-neutral-400">
              Redirigiendote al inicio de sesion en unos segundos...
            </div>
            <Button
              fullWidth
              variant="primary"
              onClick={() => navigate('/login')}
            >
              Ir al inicio de sesion ahora
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Nueva contrasena (min. 6 caracteres)"
              type="password"
              placeholder="********"
              leftIcon={<Lock className="w-4 h-4 text-neutral-400" />}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || !isValidSession}
            />

            <Input
              label="Confirmar nueva contrasena"
              type="password"
              placeholder="********"
              leftIcon={<Lock className="w-4 h-4 text-neutral-400" />}
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading || !isValidSession}
            />

            <Button
              fullWidth
              variant="primary"
              size="lg"
              type="submit"
              disabled={isLoading || !isValidSession || !password || !confirmPassword}
            >
              {isLoading ? 'Restableciendo...' : 'Restablecer contrasena'}
            </Button>

            {!isValidSession && (
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 mt-2 transition-colors focus:outline-none"
                onClick={() => navigate('/forgot-password')}
              >
                Solicitar nuevo enlace de recuperacion
              </button>
            )}
          </form>
        )}
      </Card>
    </div>
  )
}
